import { useState } from 'react'
import { PageLoader } from '../../shared/ui/Spinner'
import { ActiveRequestsTable } from './manager/ActiveRequestsTable'
import { ClientCategoriesChart } from './manager/ClientCategoriesChart'
import { EventsFeed } from './manager/EventsFeed'
import { ManagerFunnel } from './manager/ManagerFunnel'
import { ManagerHeader } from './manager/ManagerHeader'
import { ManagerKpiCards } from './manager/ManagerKpiCards'
import { ManagerPlanFactCard } from './manager/ManagerPlanFactCard'
import { OrderedClientsTable } from './manager/OrderedClientsTable'
import { TimeToKpChart } from './manager/TimeToKpChart'
import { TopBrandsTable } from './manager/TopBrandsTable'
import { ClientCategoryModal } from './manager/modals/ClientCategoryModal'
import { RequestDetailModal } from './manager/modals/RequestDetailModal'
import { useManagerDashboard, useManagerPeriod } from './manager/useManagerDashboard'
import { NewsFeedWidget } from '../news/NewsFeedWidget'
import { FollowUpAlertsPanel } from './FollowUpAlertsPanel'

// 28.04: убрал ManagerStatusBar (курс валют + кнопка «Режим КП») —
// курс уже в шапке проекта; кнопка дублировала переход в /table.
// TopParts заменён на TopBrands, поэтому PartHistoryModal больше не
// открывается с дашборда (история партномера осталась на странице
// заявки/позиции).

type ModalState =
  | { type: 'request'; id: string }
  | { type: 'category'; category: 'A' | 'B' | 'C' }
  | null

export function ManagerDashboard() {
  const { state: period, setKey } = useManagerPeriod()
  const { data, isLoading, isError, dataUpdatedAt, refetch } = useManagerDashboard(period)
  const [modal, setModal] = useState<ModalState>(null)

  if (isLoading) return <PageLoader />
  if (isError || !data) {
    return (
      <div className="bg-white rounded-2xl border border-red-200 p-6 text-center">
        <p className="text-sm text-red-700 mb-2">Не удалось загрузить дашборд</p>
        <button
          onClick={() => refetch()}
          className="px-3 py-1.5 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700"
        >
          Повторить
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <ManagerHeader
        managerName={data.manager.name}
        clientsCount={data.manager.clientsCount}
        period={period.key}
        onPeriodChange={setKey}
        fetchedAt={dataUpdatedAt}
      />

      {/* «Контроль КП»: просроченные/сегодняшние контакты с клиентами по моим заявкам. */}
      <FollowUpAlertsPanel />

      <ManagerPlanFactCard />

      <OrderedClientsTable clients={data.orderedClients} />

      <ManagerKpiCards kpi={data.kpi} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ManagerFunnel funnel={data.funnel} conversion={data.conversion} />
        <TimeToKpChart data={data.avgTimeToKp} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3">
          <ActiveRequestsTable
            requests={data.activeRequestsList}
            onRowClick={(id) => setModal({ type: 'request', id })}
          />
        </div>
        <div className="lg:col-span-2">
          <ClientCategoriesChart
            categories={data.clientCategories}
            onCategoryClick={(category) => setModal({ type: 'category', category })}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TopBrandsTable topBrands={data.topParts} />
        <EventsFeed events={data.events} />
      </div>

      <NewsFeedWidget />

      {modal?.type === 'request' && (
        <RequestDetailModal requestId={modal.id} onClose={() => setModal(null)} />
      )}
      {modal?.type === 'category' && (
        <ClientCategoryModal category={modal.category} onClose={() => setModal(null)} />
      )}
    </div>
  )
}
